import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, isBefore, parseISO } from 'date-fns';
import { ChevronDown, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { useLocationContext } from '../../components/Layout';
import {
  planVsActualReportApi,
  type DashboardSummary,
  type PlanVsActualWeekReportResponse,
} from '../../services/api';
import type { Employee, HolidayRequest, PayRecord, Schedule, Timecard } from '../../types';
import { buildActivePayRecordMap } from '../../utils/laborCost';
import {
  buildEmployeeOptions,
  buildEmployeeStatSections,
  eachWeekStartInRange,
  formatWeekRangeLabel,
  getWeekBounds,
} from '../../utils/overviewDashboardMetrics';
import DashboardCard from './DashboardCard';
import EmployeeAvatar from './EmployeeAvatar';
import {
  exportIndividualEmployeeExcel,
  exportIndividualEmployeePdf,
} from './exportIndividualEmployeeReport';
import { buildIndividualEmployeeExportPayload } from './buildIndividualEmployeeExport';
import type { EmployeeOption, EmployeeStatItem } from './types';

const MAX_WEEKS = 16;
const AVATAR_COLORS = ['bg-blue-500', 'bg-pink-500', 'bg-violet-500', 'bg-teal-500', 'bg-amber-500', 'bg-indigo-500'];

interface IndividualEmployeeReportProps {
  dashboardWeek: Date;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  holidayRequests: HolidayRequest[];
  summary: DashboardSummary | null;
  employeeDirectory: Employee[];
  /** @deprecated Overview no longer seeds PVA; kept for API compat. */
  seedReport?: PlanVsActualWeekReportResponse | null;
  loading?: boolean;
}

async function loadWeekReport(
  weekCursor: Date,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  locationId?: number | null,
): Promise<PlanVsActualWeekReportResponse> {
  const { weekStart, weekEnd, apiWeekStart } = getWeekBounds(weekCursor, weekStartsOn);
  return planVsActualReportApi.getWeekReport({
    weekStartDate: format(apiWeekStart, 'yyyy-MM-dd'),
    weekStartDisplay: format(weekStart, 'yyyy-MM-dd'),
    weekEndDisplay: format(weekEnd, 'yyyy-MM-dd'),
    locationId: locationId ?? undefined,
  });
}

function mergeReports(reports: PlanVsActualWeekReportResponse[]): {
  timecards: Timecard[];
  employees: PlanVsActualWeekReportResponse['employees'];
  schedules: Schedule[];
  payRecords: PayRecord[];
} {
  const timecardByEmployee = new Map<number, Timecard>();
  const employeeById = new Map<number, PlanVsActualWeekReportResponse['employees'][number]>();
  const scheduleById = new Map<number, Schedule>();
  const payById = new Map<number, PayRecord>();

  reports.forEach((report) => {
    report.employees.forEach((e) => employeeById.set(e.id, e));
    report.schedules.forEach((s) => scheduleById.set(s.id, s));
    report.payRecords.forEach((p) => payById.set(p.id, p));
    report.timecards.forEach((tc) => {
      const existing = timecardByEmployee.get(tc.employeeId);
      if (!existing) {
        timecardByEmployee.set(tc.employeeId, { ...tc, days: [...(tc.days ?? [])] });
      } else {
        existing.days = [...(existing.days ?? []), ...(tc.days ?? [])];
      }
    });
  });

  return {
    timecards: Array.from(timecardByEmployee.values()),
    employees: Array.from(employeeById.values()),
    schedules: Array.from(scheduleById.values()),
    payRecords: Array.from(payById.values()),
  };
}

const dateInputClass =
  'text-xs text-gray-700 border border-gray-200 rounded-md px-2 py-1 bg-white disabled:bg-gray-50';

/**
 * Does not call plan-vs-actual on Overview mount.
 * PVA loads only when the user clicks Load report (keeps week nav free of fat requests).
 */
const IndividualEmployeeReport: React.FC<IndividualEmployeeReportProps> = ({
  dashboardWeek,
  weekStartsOn,
  holidayRequests,
  summary,
  employeeDirectory,
}) => {
  const { debouncedSelectedLocationId } = useLocationContext();
  const dashboardBounds = useMemo(
    () => getWeekBounds(dashboardWeek, weekStartsOn),
    [dashboardWeek, weekStartsOn],
  );

  const [fromDate, setFromDate] = useState(() => format(dashboardBounds.weekStart, 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(() => format(dashboardBounds.weekEnd, 'yyyy-MM-dd'));
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [merged, setMerged] = useState<ReturnType<typeof mergeReports> | null>(null);
  const [loadGeneration, setLoadGeneration] = useState(0);

  useEffect(() => {
    setFromDate(format(dashboardBounds.weekStart, 'yyyy-MM-dd'));
    setToDate(format(dashboardBounds.weekEnd, 'yyyy-MM-dd'));
    setMerged(null);
    setRangeError(null);
    setLoadGeneration(0);
  }, [dashboardBounds.weekStart, dashboardBounds.weekEnd]);

  const rangeStart = useMemo(() => {
    try {
      return parseISO(fromDate <= toDate ? fromDate : toDate);
    } catch {
      return dashboardBounds.weekStart;
    }
  }, [fromDate, toDate, dashboardBounds.weekStart]);

  const rangeEnd = useMemo(() => {
    try {
      return parseISO(fromDate <= toDate ? toDate : fromDate);
    } catch {
      return dashboardBounds.weekEnd;
    }
  }, [fromDate, toDate, dashboardBounds.weekEnd]);

  const periodLabel = formatWeekRangeLabel(rangeStart, rangeEnd);

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    if (value && toDate && isBefore(parseISO(toDate), parseISO(value))) {
      setToDate(value);
    }
    setMerged(null);
    setLoadGeneration(0);
  };

  const handleToDateChange = (value: string) => {
    setToDate(value);
    if (value && fromDate && isBefore(parseISO(value), parseISO(fromDate))) {
      setFromDate(value);
    }
    setMerged(null);
    setLoadGeneration(0);
  };

  const directoryOptions: EmployeeOption[] = useMemo(() => {
    const scoped = debouncedSelectedLocationId
      ? employeeDirectory.filter(
          (e) =>
            e.locationId === debouncedSelectedLocationId ||
            e.profile?.mainLocationId === debouncedSelectedLocationId,
        )
      : employeeDirectory;
    return scoped
      .filter((e) => e.isActive !== false)
      .slice(0, 300)
      .map((e) => ({
        id: e.id,
        name: `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim() || `Employee ${e.id}`,
        role: '',
        initials: `${e.firstName?.[0] ?? ''}${e.lastName?.[0] ?? ''}`.toUpperCase() || '?',
        avatarColor: AVATAR_COLORS[e.id % AVATAR_COLORS.length],
        profileImageUrl: e.profileImageUrl,
        updatedAt: e.updatedAt,
      }));
  }, [employeeDirectory, debouncedSelectedLocationId]);

  useEffect(() => {
    if (directoryOptions.length === 0) {
      setSelectedId(null);
      return;
    }
    if (selectedId == null || !directoryOptions.some((o) => o.id === selectedId)) {
      setSelectedId(directoryOptions[0].id);
    }
  }, [directoryOptions, selectedId]);

  useEffect(() => {
    if (loadGeneration === 0) return;
    let cancelled = false;

    const load = async () => {
      const weeks = eachWeekStartInRange(rangeStart, rangeEnd, weekStartsOn);
      if (weeks.length === 0) return;
      if (weeks.length > MAX_WEEKS) {
        setRangeError(`Select a range of at most ${MAX_WEEKS} weeks.`);
        setMerged(null);
        return;
      }

      setRangeError(null);
      setRangeLoading(true);
      try {
        const reports = await Promise.all(
          weeks.map((w) => loadWeekReport(w, weekStartsOn, debouncedSelectedLocationId)),
        );
        if (!cancelled) setMerged(mergeReports(reports));
      } catch (err) {
        console.error('Individual employee report range load failed:', err);
        if (!cancelled) {
          setRangeError('Unable to load employee data for the selected dates.');
          setMerged(null);
        }
      } finally {
        if (!cancelled) setRangeLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    loadGeneration,
    rangeStart,
    rangeEnd,
    weekStartsOn,
    debouncedSelectedLocationId,
  ]);

  const handleLoadReport = useCallback(() => {
    setLoadGeneration((g) => g + 1);
  }, []);

  const options: EmployeeOption[] = useMemo(() => {
    if (!merged) return directoryOptions;
    const directoryById = new Map(employeeDirectory.map((e) => [e.id, e]));
    const employees = merged.employees.map((e) => {
      const full = directoryById.get(e.id);
      return {
        id: e.id,
        firstName: e.firstName,
        lastName: e.lastName,
        isActive: e.isActive,
        locationId: e.mainLocationId,
        profileImageUrl: full?.profileImageUrl,
        updatedAt: full?.updatedAt,
        profile: full?.profile ?? { mainLocationId: e.mainLocationId },
      } as Employee;
    });
    const fromReport = buildEmployeeOptions(employees, merged.schedules, merged.timecards);
    return fromReport.length > 0 ? fromReport : directoryOptions;
  }, [merged, directoryOptions, employeeDirectory]);

  const employee = options.find((e) => e.id === selectedId) ?? options[0];

  const stats: EmployeeStatItem[] = useMemo(() => {
    if (!merged || !employee) return [];
    const directoryById = new Map(employeeDirectory.map((e) => [e.id, e]));
    const employees = merged.employees.map((e) => {
      const full = directoryById.get(e.id);
      return {
        id: e.id,
        firstName: e.firstName,
        lastName: e.lastName,
        isActive: e.isActive,
        locationId: e.mainLocationId,
        profile: full?.profile ?? { mainLocationId: e.mainLocationId },
      } as Employee;
    });
    const payRecords = buildActivePayRecordMap(merged.payRecords);
    const sections = buildEmployeeStatSections(
      employee.id,
      merged.timecards,
      employees,
      merged.schedules,
      payRecords,
      summary,
      holidayRequests,
      rangeStart,
      rangeEnd,
    );
    return sections.flatMap((s) => s.stats);
  }, [merged, employee, employeeDirectory, summary, holidayRequests, rangeStart, rangeEnd]);

  const exportPayload = useMemo(() => {
    if (!merged || !employee || stats.length === 0) return null;
    return buildIndividualEmployeeExportPayload({
      employeeId: employee.id,
      employeeName: employee.name,
      role: employee.role,
      periodLabel,
      fromDate,
      toDate,
      rangeStart,
      rangeEnd,
      summaryMetrics: stats,
      timecards: merged.timecards,
      payRecords: buildActivePayRecordMap(merged.payRecords),
      holidayRequests,
      summary,
    });
  }, [
    merged,
    employee,
    stats,
    periodLabel,
    fromDate,
    toDate,
    rangeStart,
    rangeEnd,
    holidayRequests,
    summary,
  ]);

  const canExport = !!exportPayload && !rangeLoading;
  const needsLoad = loadGeneration === 0 || !merged;

  return (
    <DashboardCard
      title="Individual Employee Report"
      className="h-full"
      action={
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            title="Export to Excel"
            disabled={!canExport}
            onClick={() => exportPayload && exportIndividualEmployeeExcel(exportPayload)}
            className="p-1 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:text-gray-300 disabled:hover:bg-transparent"
          >
            <FileSpreadsheet className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Export to PDF"
            disabled={!canExport}
            onClick={() => exportPayload && exportIndividualEmployeePdf(exportPayload)}
            className="p-1 rounded-md text-rose-600 hover:bg-rose-50 disabled:text-gray-300 disabled:hover:bg-transparent"
          >
            <FileText className="h-4 w-4" />
          </button>
          {employee ? (
            <div className="relative ml-0.5">
              <select
                value={employee.id}
                onChange={(e) => setSelectedId(Number(e.target.value))}
                className="appearance-none text-xs text-gray-600 border border-gray-200 rounded-md pl-2.5 pr-7 py-1 hover:bg-gray-50 bg-white cursor-pointer max-w-[140px] truncate"
              >
                {options.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
            </div>
          ) : null}
        </div>
      }
    >
      <div className="px-5 pb-5">
        {rangeError && <p className="text-xs text-red-600 mb-3">{rangeError}</p>}

        <div className="mb-5">
          <div className="flex items-center justify-between gap-2">
            <label className="flex flex-col gap-1 min-w-0 flex-1 max-w-[110px]">
              <span className="text-[10px] font-medium text-gray-500">From Date</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                disabled={rangeLoading}
                className={`${dateInputClass} w-full min-w-0`}
              />
            </label>

            {employee ? (
              <EmployeeAvatar
                profileImageUrl={employee.profileImageUrl}
                updatedAt={employee.updatedAt}
                initials={employee.initials}
                avatarColor={employee.avatarColor}
                size="lg"
                className="flex-shrink-0"
              />
            ) : (
              <div className="h-14 w-14 rounded-full bg-gray-100 flex-shrink-0" />
            )}

            <label className="flex flex-col gap-1 min-w-0 flex-1 max-w-[110px] items-end">
              <span className="text-[10px] font-medium text-gray-500">To Date</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                disabled={rangeLoading}
                className={`${dateInputClass} w-full min-w-0`}
              />
            </label>
          </div>
          <p className="text-[11px] text-gray-400 text-center mt-2">{periodLabel}</p>
        </div>

        {rangeLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : needsLoad ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10">
            <p className="text-sm text-gray-500 text-center px-2">
              Load this report on demand so week navigation stays fast.
            </p>
            <button
              type="button"
              onClick={handleLoadReport}
              disabled={!employee}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Load report
            </button>
          </div>
        ) : !employee ? (
          <p className="text-sm text-gray-500 text-center py-12">No employees in this location.</p>
        ) : (
          <div className="space-y-2">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-center justify-between text-sm border-b border-gray-50 py-1.5 last:border-0"
              >
                <span className="text-gray-500">{stat.label}</span>
                <span className="font-medium text-gray-900">{stat.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardCard>
  );
};

export default IndividualEmployeeReport;
